import React, { Fragment, FC } from 'react'
import classNamesBind from 'classnames/bind'
import Link from 'next/link'

import Flex from '@dashboard/components/Flex'
import ComparisonProfileImage from '@dashboard/components/ComparisonProfileImage'
import { fullName } from '@dashboard/lib/profile'
import { Profile } from '@dashboard/lib/types'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

interface OwnProps {
  celebrity: Profile
  personalityType?: string
}

const CelebrityPill: FC<OwnProps> = ({
  celebrity,
  personalityType,
}: OwnProps) => {
  const name = fullName(celebrity)
  const profileId = celebrity.vanity_url
  const profileHref = `/personality/${profileId}`

  // Partials
  const renderMyersBriggsLink = (celeb: Profile) => {
    if (!celeb.myers_briggs_text_type) return null

    return (
      <Fragment>
        <div className={classNames('dotSeparator')} />
        {renderCelebPersonalityLink(
          '/myers-briggs/[personalityType]/famous-people',
          `/myers-briggs/${celeb.myers_briggs_text_type.toLowerCase()}/famous-people`,
          celeb.myers_briggs_text_type,
        )}
      </Fragment>
    )
  }

  const renderDiscTypeLink = (celeb: Profile) => {
    const { personality } = celeb

    if (!personality || !personality.disc_type) return null

    const discType = personality.disc_type
    const deserializedDiscType = /[A-Z]{2}/.test(discType)
      ? discType.split('').join('-').toLowerCase()
      : discType.toLowerCase()

    return (
      <Fragment>
        <div className={classNames('dotSeparator')} />
        {renderCelebPersonalityLink(
          '/disc/[personalityType]/famous-people',
          `/disc/${deserializedDiscType}/famous-people`,
          discType,
        )}
      </Fragment>
    )
  }

  const renderEnneagramLink = (celeb: Profile) => {
    if (!celeb.enneagram_text_type) return null

    return (
      <Fragment>
        <div className={classNames('dotSeparator')} />
        {renderCelebPersonalityLink(
          '/enneagram/[personalityType]/famous-people',
          `/enneagram/${celeb.enneagram_text_type.toLowerCase()}/famous-people`,
          celeb.enneagram_text_type,
        )}
      </Fragment>
    )
  }

  const renderCelebPersonalityLink = (
    href: string,
    as: string,
    text: string,
  ) => {
    return (
      <Link
        href={href}
        as={as}
        className={classNames(
          'celebInfo',
          `type-${personalityType || 'empty'}`,
        )}
        passHref
      >
        {text}
      </Link>
    )
  }

  return (
    <div className={classNames('container')}>
      <Flex alignCenter>
        <Link href="/personality/[profileId]" as={profileHref} passHref>
          <div className={classNames('profileImageContainer')}>
            <ComparisonProfileImage
              photoURL={celebrity.photo_url}
              fullName={name}
              size={75}
            />
          </div>
        </Link>
        <div
          className={classNames(
            'celebrityPill',
            `type-${personalityType || 'empty'}`,
          )}
        >
          <Flex vertical>
            <div className={classNames('celebName')}>{name}</div>
            <Flex alignCenter>
              <Link
                href="/personality/[profileId]"
                as={profileHref}
                className={classNames(
                  'celebInfo',
                  `type-${personalityType || 'empty'}`,
                )}
                passHref
              >
                Profile
              </Link>
              {renderMyersBriggsLink(celebrity)}
              {renderDiscTypeLink(celebrity)}
              {renderEnneagramLink(celebrity)}
            </Flex>
          </Flex>
        </div>
      </Flex>
    </div>
  )
}

export default CelebrityPill
